import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import axios from 'axios'
import AddModal from '../category/add_modal'
import SectionItems from '../category/section_items'
import { useConfirm } from '../../contexts/confirm_dialog'
import { useSnackbar } from '../../hooks/use_snackbar'
import useAuth from '../../hooks/use_auth'
import { modelTypes } from '../../constants/model_types'

const InterstitalAdsTable = ({ cycle }) => {
  const confirm = useConfirm()
  const { addMessage } = useSnackbar()
  const { canEditModel: canEditCycle } = useAuth(modelTypes.Cycle, cycle)
  const { data: airline } = useQuery([`airlines/${cycle.airline_id}`])
  const interstitialsQueryKey = `/categories?cycleID=${cycle.id}&type=interstitial`
  const queryClient = useQueryClient()
  const { data: intersitials, isLoading, isRefetching, error, refetch } = useQuery([interstitialsQueryKey])
  const [showModal, setShowModal] = useState(false)

  const childCategoryId = intersitials?.[0]?.child_categories?.[0]?.id
  const interstitialCategory = intersitials?.find((cat) => cat.id === childCategoryId)
  const items = interstitialCategory?.category_items ?? []

  const queryKey = `categories/${interstitialCategory?.id}/get-addable-items?type=assets&adAssignment`

  const itemPath = (item) => {
    const langCode = airline?.languages?.find((lang) => lang.id === airline.default_language_id)?.ife_code || 'ENG'
    return `/airlines/${cycle.airline_id}/cycles/${cycle.id}/ads/clips/${item.id}/details`
  }

  const updateOrder = useMutation(
    ({ ids }) =>
      axios.put(`/categories/${interstitialCategory?.id}/update-item-order`, {
        ids,
        startIndex: 1,
        type: interstitialCategory?.type,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(interstitialsQueryKey)
      },
      onError: (err) => {
        addMessage(err?.response?.data?.error ?? err.message, 'error')
      },
    }
  )

  const removeItems = useMutation(
    (ids) =>
      axios.delete(`/categories/${interstitialCategory?.id}/remove-items`, {
        data: { ids, type: interstitialCategory?.type },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(interstitialsQueryKey)
      },
      onError: (err) => {
        addMessage(err?.response?.data?.error ?? err.message, 'error')
      },
    }
  )

  const moveItem = (index, offset) => {
    const order = [...items]
    order.splice(index + offset, 0, order.splice(index, 1)[0])
    updateOrder.mutate({ ids: order.map((item) => item.id) })
  }

  const removeItem = async (item) => {
    const accepted = await confirm({
      title: 'Remove interstitial ad?',
      message: `Removes “${item.title}” from this cycle. The ad stays in the cycle.`,
      confirmText: 'Remove',
      confirmVariant: 'destructive',
    })
    if (accepted) removeItems.mutate([item.id])
  }

  return (
    <>
      <SectionItems
        title='Interstitial ads'
        description='Ads and the order they appear as interstitials in this cycle.'
        emptyText='No interstitial ads yet.'
        addLabel='Add interstitial ad'
        removeLabel={(name) => `Remove ${name} from interstitials`}
        items={items}
        isLoading={isLoading}
        isFetching={isRefetching}
        error={error}
        onRetry={refetch}
        getItemName={(item) => item.title}
        subtitle={(_item, dates) => dates || null}
        itemPath={itemPath}
        canReorder={canEditCycle && !cycle.is_locked}
        onMove={moveItem}
        onRemove={removeItem}
        onAdd={() => setShowModal(true)}
      />
      {showModal && interstitialCategory && (
        <AddModal
          category={interstitialCategory}
          invalidateKey={interstitialsQueryKey}
          onClose={() => setShowModal(false)}
          queryKey={[queryKey]}
          type='interstitial'
          title='Add interstitial ad'
        />
      )}
    </>
  )
}

export default InterstitalAdsTable
